import {FieldNames} from "models/fields";
import {SEVERITY_MAP, SORT_ORDER_MAP} from "models/options";

/**
 * Manually sorts table data by nested time fields
 * (e.g. observed_time.start_time)
 *
 * @param {Array<Object>} values values to sort
 * @param {('DESC'|'ASC')} order sort direction
 * @param {string} field1 field to be used as key for nested field
 * @param {string} field2 nested field
 * @returns {Array<Object>} sorted values
 */
export const sortByNestedTime = (values = [], order, field1, field2) =>
  values && values.length > 0
    ? [...values].sort((a, b) => {
        const first = order === SORT_ORDER_MAP.desc.value ? b : a;
        const last = order === SORT_ORDER_MAP.desc.value ? a : b;
        const date1 = first[field1][field2];
        const date2 = last[field1][field2];
        return date1 < date2 ? -1 : date1 > date2 ? 1 : 0;
      })
    : [];

/**
 * Manually sorts table data based on the severity field
 *
 * @param {Array<{severity: string}>} values values to sort
 * @param {('DESC'|'ASC')} order sort direction
 * @param {string} [field=FieldNames.severity] optional field to use instead of severity
 * @returns {Array<Object>} sorted values
 */
export const sortBySeverity = (
  values = [],
  order = SORT_ORDER_MAP.desc.value,
  field = FieldNames.severity
) =>
  [...values].sort((a, b) => {
    const priorityA = SEVERITY_MAP[a[field]]?.priority || 0;
    const priorityB = SEVERITY_MAP[b[field]]?.priority || 0;
    return order === SORT_ORDER_MAP.asc.value
      ? priorityA - priorityB
      : priorityB - priorityA;
  });

/**
 * Manually paginates table data
 *
 * @param {Array<object>} values values to sort
 * @param {number} page current page number
 * @param {number} pageCount number of items per page
 * @returns {Array<object>} page of values
 */
export const paginateTableData = (values, page, pageCount) => {
  const start = (page - 1) * pageCount;
  const end = page * pageCount;
  return values ? values.slice(start, end) : [];
};

/**
 * Loading skeleton for table cells
 *
 * @param {number} rows number of rows needed
 * @returns {Array<object>} Array of cells
 */
export const tableLoadingState = (rows = 10) =>
  new Array(rows).fill({cellLoading: true});
